import { useState, useEffect, useRef } from "react";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

export default function BookingWidget() {
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [promoCode, setPromoCode] = useState("");
  
  const [showGuests, setShowGuests] = useState(false);
  const [checkinError, setCheckinError] = useState(false);
  const [checkoutError, setCheckoutError] = useState(false);

  const checkinRef = useRef<HTMLInputElement>(null);
  const checkoutRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const checkinInstanceRef = useRef<any>(null);
  const checkoutInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (checkinRef.current && checkoutRef.current) {
      checkinInstanceRef.current = flatpickr(checkinRef.current, {
        minDate: "today",
        dateFormat: "Y-m-d",
        disableMobile: true,
        clickOpens: false, // Prevent opening automatically on click since we toggle it manually
        onChange: function (selectedDates: Date[], dateStr: string) {
          setCheckin(dateStr);
          setCheckinError(false);
          if (checkoutInstanceRef.current && selectedDates.length > 0) {
            const nextDay = new Date(selectedDates[0]);
            nextDay.setDate(nextDay.getDate() + 1);
            checkoutInstanceRef.current.set('minDate', nextDay);
            
            // Reset checkout if it is equal or prior to the check-in date
            if (checkoutRef.current && checkoutRef.current.value) {
              const currentCheckoutVal = new Date(checkoutRef.current.value);
              if (currentCheckoutVal <= selectedDates[0]) {
                setCheckout("");
                checkoutRef.current.value = "";
              }
            }
          }
        }
      });

      checkoutInstanceRef.current = flatpickr(checkoutRef.current, {
        minDate: "today",
        dateFormat: "Y-m-d",
        disableMobile: true,
        clickOpens: false, // Prevent opening automatically on click since we toggle it manually
        onChange: function (_selectedDates: Date[], dateStr: string) {
          setCheckout(dateStr);
          setCheckoutError(false);
        }
      });
    }

    // Close guest dropdown on click outside
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setShowGuests(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      if (checkinInstanceRef.current) checkinInstanceRef.current.destroy();
      if (checkoutInstanceRef.current) checkoutInstanceRef.current.destroy();
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleBook = () => {
    let isValid = true;
    if (!checkin) {
      setCheckinError(true);
      isValid = false;
    }
    if (!checkout) {
      setCheckoutError(true);
      isValid = false;
    }

    if (isValid) {
      const link = "https://secure.guestpro.net/";
      const merchant_id = "Patalkikian";
      const promoStr = promoCode ? encodeURIComponent(promoCode) : "promo_code_empty";
      const redirectUrl = `${link}${merchant_id}/hotel-filter-redirect/${checkin}/${checkout}/${promoStr}?guest=${adults}-${children}-${infants}`;
      window.open(redirectUrl, '_blank');
    }
  };

  const updateGuest = (type: "adult" | "children" | "infant", change: number) => {
    if (type === "adult") {
      setAdults(prev => Math.max(1, Math.min(20, prev + change)));
    } else if (type === "children") {
      setChildren(prev => Math.max(0, Math.min(20, prev + change)));
    } else if (type === "infant") {
      setInfants(prev => Math.max(0, Math.min(20, prev + change)));
    }
  };

  return (
    <div className="booking-widget-container w-full max-w-6xl mx-auto px-6 relative z-[40]">
      {/* Dynamic Style Block */}
      <style>{`
        :root {
          --booking-bg: #ffffff;
          --booking-border: rgba(0, 0, 0, 0.08);
          --booking-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
          --booking-text-primary: #1d1d1f;
          --booking-text-secondary: #86868b;
          --booking-accent: #B68D40; /* Matched the luxury gold color */
          --booking-btn-bg: #1d1d1f;
          --booking-btn-bg-hover: #323236;
          --booking-btn-text: #ffffff;
          --booking-danger: #ff3b30;
          --booking-radius: 18px;
        }

        .box-be-custom {
          background-color: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          border-radius: var(--booking-radius);
          padding: 16px 24px;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 16px;
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          position: relative;
        }

        .box-be-custom:hover {
          box-shadow: 0 16px 50px rgba(0, 0, 0, 0.15);
          background-color: rgba(255, 255, 255, 0.2);
        }

        .field-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          position: relative;
          min-width: 130px;
        }

        .field-wrapper.guest-field {
          flex: 1.4;
          min-width: 200px;
        }

        .field-wrapper.promo-field {
          flex: 1.1;
          min-width: 140px;
        }

        .button-wrapper {
          flex: 0 0 auto;
          display: flex;
          align-items: flex-end;
          align-self: flex-end;
        }

        .field-wrapper label {
          font-size: 11px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }

        .input-container {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
          cursor: pointer;
        }

        .input-be {
          width: 100%;
          height: 48px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          padding: 0 40px 0 16px;
          background-color: rgba(255, 255, 255, 0.95);
          color: var(--booking-text-primary);
          font-size: 14px;
          font-weight: 500;
          border-radius: 12px;
          outline: none;
          transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          box-sizing: border-box;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }

        .input-be:hover {
          border-color: #86868b;
          background-color: #f5f5f7;
        }

        .input-be:focus, 
        .input-be.active {
          background-color: #ffffff;
          border-color: var(--booking-accent);
          box-shadow: 0 0 0 4px rgba(182, 141, 64, 0.15);
        }

        .input-icon-svg {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--booking-text-secondary);
          pointer-events: none;
          transition: color 0.2s ease, transform 0.2s ease;
          z-index: 2;
        }

        .input-be.active + .input-icon-svg,
        .input-be:focus + .input-icon-svg {
          color: var(--booking-accent);
        }

        .guest-summary-text {
          margin: 0;
          font-size: 13.5px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: calc(100% - 10px);
          pointer-events: none;
        }

        .btn-book-now {
          color: var(--booking-btn-text);
          background-color: var(--booking-btn-bg);
          border: none;
          border-radius: 12px;
          padding: 0 32px;
          font-weight: 600;
          font-size: 14px;
          height: 48px;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
        }

        .btn-book-now:hover {
          background-color: var(--booking-btn-bg-hover);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .btn-book-now:active {
          transform: translateY(0) scale(0.985);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .error-text {
          font-size: 11px;
          color: var(--booking-danger);
          margin-top: 4px;
          font-weight: 500;
          position: absolute;
          top: 100%;
          left: 0;
        }

        .pop-up-guest {
          background-color: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          padding: 16px 20px;
          border: 1px solid var(--booking-border);
          border-radius: 16px;
          width: 100%;
          min-width: 260px;
          box-sizing: border-box;
          box-shadow: 0 15px 45px rgba(0, 0, 0, 0.1);
          position: absolute;
          top: calc(100% + 12px);
          bottom: auto;
          left: 0;
          z-index: 100;
          transform-origin: top left;
        }

        .guest-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
        }

        .guest-row:not(:last-child) {
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .guest-info .title {
          margin: 0 0 2px 0;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--booking-text-primary);
        }

        .guest-info .description {
          margin: 0;
          font-size: 11.5px;
          color: var(--booking-text-secondary);
        }

        .counter-control {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .counter-btn {
          all: unset;
          box-sizing: border-box;
          border: 1px solid #d2d2d7;
          background-color: #ffffff;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--booking-text-primary);
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          user-select: none;
        }

        .counter-btn:hover {
          background-color: #f5f5f7;
          border-color: #86868b;
        }

        .counter-btn.disabled {
          opacity: 0.25;
          cursor: not-allowed;
          pointer-events: none;
        }

        .counter-value {
          font-size: 14.5px;
          font-weight: 600;
          min-width: 16px;
          text-align: center;
          color: var(--booking-text-primary) !important;
        }

        /* Responsive (Tablet & Mobile) */
        @media (min-width: 601px) and (max-width: 991px) {
          .box-be-custom {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            padding: 16px;
          }
          .field-wrapper {
            width: 100%;
          }
          .input-be {
            height: 42px;
            font-size: 13.5px;
          }
          .field-wrapper label {
            margin-bottom: 4px;
          }
          .button-wrapper {
            grid-column: span 2;
            margin-top: 4px;
          }
          .btn-book-now {
            width: 100%;
            height: 42px;
          }
          .pop-up-guest {
            position: absolute !important;
            top: calc(100% + 8px) !important;
            bottom: auto !important;
            left: 0 !important;
            transform: none !important;
            width: 100% !important;
            max-width: none !important;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
            background-color: #ffffff !important;
            transform-origin: top left !important;
          }
        }

        @media (max-width: 600px) {
          .box-be-custom {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            padding: 12px;
          }
          .field-wrapper {
            width: 100%;
          }
          .field-wrapper.guest-field, 
          .field-wrapper.promo-field, 
          .button-wrapper {
            grid-column: span 2;
          }
          .input-be {
            height: 40px;
            font-size: 13px;
          }
          .field-wrapper label {
            margin-bottom: 3px;
          }
          .button-wrapper {
            margin-top: 4px;
          }
          .btn-book-now {
            width: 100%;
            height: 40px;
          }
          .pop-up-guest {
            position: absolute !important;
            top: calc(100% + 8px) !important;
            bottom: auto !important;
            left: 0 !important;
            transform: none !important;
            width: 100% !important;
            max-width: none !important;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
            background-color: #ffffff !important;
            transform-origin: top left !important;
          }
        }
      `}</style>

      {/* Booking Bar */}
      <div className="box-be-custom">
        {/* Check In */}
        <div className="field-wrapper">
          <label>Check in</label>
          <div 
            className="input-container"
            onClick={(e) => {
              e.stopPropagation();
              setShowGuests(false);
              checkoutInstanceRef.current?.close();
              const instance = checkinInstanceRef.current;
              if (instance) {
                if (instance.isOpen) {
                  instance.close();
                } else {
                  instance.open();
                }
              }
            }}
          >
            <input
              ref={checkinRef}
              type="text"
              className={`input-be ${checkinError ? "border-red-500" : ""}`}
              required
              placeholder="Select date"
              readOnly
            />
            <svg className="input-icon-svg" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          {checkinError && <p className="error-text">Please select check-in date</p>}
        </div>
        
        {/* Check Out */}
        <div className="field-wrapper">
          <label>Check out</label>
          <div 
            className="input-container"
            onClick={(e) => {
              e.stopPropagation();
              setShowGuests(false);
              checkinInstanceRef.current?.close();
              const instance = checkoutInstanceRef.current;
              if (instance) {
                if (instance.isOpen) {
                  instance.close();
                } else {
                  instance.open();
                }
              }
            }}
          >
            <input
              ref={checkoutRef}
              type="text"
              className={`input-be ${checkoutError ? "border-red-500" : ""}`}
              required
              placeholder="Select date"
              readOnly
            />
            <svg className="input-icon-svg" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          {checkoutError && <p className="error-text">Please select check-out date</p>}
        </div>
        
        {/* Guests */}
        <div className="field-wrapper guest-field" ref={triggerRef}>
          <label>Guests</label>
          <div 
            className="input-container" 
            onClick={(e) => {
              e.stopPropagation();
              checkinInstanceRef.current?.close();
              checkoutInstanceRef.current?.close();
              setShowGuests(!showGuests);
            }}
          >
            <div className={`input-be ${showGuests ? "active" : ""}`}>
              <p className="guest-summary-text text-left">
                {adults} Adult{adults > 1 ? "s" : ""}, {children} Child{children !== 1 ? "ren" : ""}, {infants} Infant{infants !== 1 ? "s" : ""}
              </p>
            </div>
            <svg className={`input-icon-svg transition-transform duration-200 ${showGuests ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Pop Up Guest Selection */}
          {showGuests && (
            <div className="pop-up-guest" ref={popupRef} onClick={(e) => e.stopPropagation()}>
              {/* Adults */}
              <div className="guest-row">
                <div className="guest-info text-left">
                  <p className="title">Adults</p>
                  <p className="description">Ages 17 or above</p>
                </div>
                <div className="counter-control">
                  <button type="button" className={`counter-btn ${adults === 1 ? "disabled" : ""}`} onClick={() => updateGuest("adult", -1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                    </svg>
                  </button>
                  <span className="counter-value">{adults}</span>
                  <button type="button" className="counter-btn" onClick={() => updateGuest("adult", 1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12M6 12h12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Children */}
              <div className="guest-row">
                <div className="guest-info text-left">
                  <p className="title">Children</p>
                  <p className="description">Ages 2–16</p>
                </div>
                <div className="counter-control">
                  <button type="button" className={`counter-btn ${children === 0 ? "disabled" : ""}`} onClick={() => updateGuest("children", -1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                    </svg>
                  </button>
                  <span className="counter-value">{children}</span>
                  <button type="button" className="counter-btn" onClick={() => updateGuest("children", 1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12M6 12h12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Infants */}
              <div className="guest-row">
                <div className="guest-info text-left">
                  <p className="title">Infants</p>
                  <p className="description">Under 2</p>
                </div>
                <div className="counter-control">
                  <button type="button" className={`counter-btn ${infants === 0 ? "disabled" : ""}`} onClick={() => updateGuest("infant", -1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
                    </svg>
                  </button>
                  <span className="counter-value">{infants}</span>
                  <button type="button" className="counter-btn" onClick={() => updateGuest("infant", 1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12M6 12h12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Promo Code */}
        <div className="field-wrapper promo-field">
          <label>Promo Code</label>
          <input
            type="text"
            className="input-be"
            placeholder="Enter code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            autoComplete="off"
          />
        </div>
        
        {/* Book Now Button */}
        <div className="button-wrapper">
          <button type="button" onClick={handleBook} className="btn-book-now">Book Now</button>
        </div>
      </div>
    </div>
  );
}
